import { useEffect } from 'react'
import { getWindow } from '@ztechapps/common.utils'

export type UseFocusTrapProps = {
  getContainer?: () => HTMLElement | null | undefined
  isActive?: boolean
}

/**
 * Traps Tab/Shift+Tab focus inside a container element while active.
 * Tab on the container's last focusable element wraps to its first; Shift+Tab on the
 * first wraps to its last; Shift+Tab while the container element itself holds focus
 * (e.g. right after `autoFocus` lands on it) also moves to its last focusable element.
 * Moves no focus on activation and restores none on cleanup — the caller owns that.
 *
 * The listener is attached to the container itself, not `document`: stacked dialogs are
 * sibling portals under `document.body`, so a Tab inside one never reaches another
 * dialog's listener.
 *
 * @example
 * ```tsx
 * const modalRef = useRef<HTMLDivElement>(null)
 * useFocusTrap({ getContainer: () => modalRef.current, isActive: isOpen })
 * ```
 */
export const useFocusTrap = ({ getContainer, isActive = false }: UseFocusTrapProps = {}): void => {
  useEffect(() => {
    if (!isActive) return

    const win = getWindow()
    if (!win) return

    const container = getContainer?.()
    if (!container) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      // Get all focusable elements
      const focusableElements = container.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      )

      // Exclude elements hidden via a display:none ancestor (e.g. an inactive react-bootstrap
      // `<Tab>` pane — both panes stay mounted, node_modules/react-bootstrap/cjs/Tabs.js:60-61) —
      // getClientRects() is empty for anything not laid out.
      const focusableArray = Array.from(focusableElements).filter(
        (element) => element.tabIndex >= 0 && element.getClientRects().length > 0
      )
      const firstFocusable = focusableArray[0]
      const lastFocusable = focusableArray[focusableArray.length - 1]

      // react-bootstrap's `autoFocus` focuses the dialog element itself on show
      // (node_modules/@restart/ui/cjs/Modal.js:122-129); Shift+Tab from there would
      // otherwise leave the dialog before ever reaching a focusable control.
      if (event.shiftKey && win.document.activeElement === container) {
        event.preventDefault()
        lastFocusable?.focus()
        return
      }

      if (!firstFocusable || !lastFocusable) return

      // Shift+Tab on first element: go to last
      if (event.shiftKey && win.document.activeElement === firstFocusable) {
        event.preventDefault()
        lastFocusable.focus()
        return
      }

      // Tab on last element: go to first
      if (!event.shiftKey && win.document.activeElement === lastFocusable) {
        event.preventDefault()
        firstFocusable.focus()
        return
      }
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
    // `getContainer` is a new closure each render, so depending on it would re-attach the
    // listener every render; the container is read once per activation, so it must be
    // mounted when `isActive` turns true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])
}
